import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import RadioGroup from 'components/final-form/RadioGroup';
import { generateRadioButtonOptions } from 'components/final-form/radioUtils';
import { selectInput } from 'components/final-form/SelectInput';
import { textInput } from 'components/final-form/TextInput';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { IndexedZipData } from 'components/patient/upsert/UpsertPatient';
import { sortZipData } from 'components/patient/utils/patientUtils';
import { FormApi } from 'final-form';
import createDecorator from 'final-form-calculate';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, RadioProps } from 'semantic-ui-react';
import { getAllCountries } from 'services/countryServices';
import { editGuarantor, hasGuarantorCompany, saveGuarantor } from 'services/guarantorServices';
import { searchZipDataForInput } from 'services/zipDataServices';
import styled from 'styled-components';
import { AlternativeTaxPointGroup, BillReceiver, Diagnosis, TariffType, ZipDataOrderBy } from 'ts-types/api.enums';
import { UpsertGuarantorDto, UpsertPatientDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';
import { required } from 'utils/validatorUtils';

const StyledGridColumn = styled(Grid.Column)`
  width: 13.75% !important
`;

interface GuarantorAdditionalInfo {
  collectiveBill: boolean,
  billDiscount: string,
  billDetail: boolean
}

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onSave: () => void,
  onCancel: () => void,
  guarantorId?: number,
  guarantor?: UpsertGuarantorDto
}

interface State {
  errorMessage?: string,
  billReceivers: RadioProps[],
  tariffTypes?: DropdownOption[],
  alternativeTaxPointGroups: DropdownOption[],
  diagnosis: RadioProps[],
  billTexts: DropdownOption[]
  countries?: DropdownOption[],
  cities: DropdownOption[],
  zip: DropdownOption[],
  disableCollectiveBillGuarantor: boolean,
  infoModalOpen: boolean,
  // guarantorAdditionalInfo?: GuarantorAdditionalInfo,
  cancelTokenSource: CancelTokenSource
}

class UpsertGuarantorForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {guarantor, getMessage} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const billReceiverLabels: string[] = ["patient", "guarantor"];
    const diagnosisLabels: string[] = ["clearText", "coded", "suppressed"];
    const billTextLabels: string[] = ["german", "french", "italian", "english"];
    const billTextKeys: string[] = ["de", "fr", "it", "en"];

    getAllCountries(cancelTokenSource)
        .then(response => {
          this.setState({
            countries: mapToDropdownOptionArray(response, "name", "shortName")
          })
        }).catch(noop);

    const billReceivers = generateRadioButtonOptions(
        billReceiverLabels, "bill", getMessage, "bill", Object.values(BillReceiver));

    const diagnosis = generateRadioButtonOptions(
        diagnosisLabels, "diagnosis", getMessage, "diagnosis", Object.values(Diagnosis));

    const initialCities: DropdownOption[] = [];
    const initialZip: DropdownOption[] = [];

    if (guarantor) {

      if (guarantor.city) {
        initialCities.push({
          key: guarantor.city,
          text: guarantor.city,
          value: guarantor.city,
          predefinedOption: true
        })
      }

      if (guarantor.zip) {
        initialZip.push({
          key: guarantor.zip,
          text: guarantor.zip,
          value: guarantor.zip,
          predefinedOption: true
        })
      }
    }

    const tariffTypes: DropdownOption[] = Object.values(TariffType).map((value: string) => {
      return {
        key: value,
        text: getMessage(`tariffType.${value.toLowerCase()}`),
        value: value
      }
    });

    const alternativeTaxPointGroups: DropdownOption[] =
        Object.values(AlternativeTaxPointGroup).map((value: string) => {
          return {
            key: value,
            text: value,
            // text: getMessage(`alternativeTaxPointGroup.${value.toLowerCase()}`),
            value: value
          }
        });

    this.state = {
      billReceivers: billReceivers,
      diagnosis: diagnosis,
      billTexts: stringArrayToDropdownOptionArray(billTextLabels, getMessage, "billText", billTextKeys),
      cities: initialCities,
      zip: initialZip,
      infoModalOpen: false,
      tariffTypes: tariffTypes,
      alternativeTaxPointGroups,
      disableCollectiveBillGuarantor: false,
      cancelTokenSource
    }

    if (props.guarantor) {
      hasGuarantorCompany(props.guarantorId!, cancelTokenSource)
          .then(response => {
            this.setState({
              disableCollectiveBillGuarantor: response
            })
          }).catch(noop)
    }
    ;
  };

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  initialGuarantorValues = (): Partial<UpsertGuarantorDto> => {

    const {countries} = this.state;

    return {
      active: true,
      bill: BillReceiver.PATIENT,
      diagnosisCode: Diagnosis.CLEAR_TEXT,
      billText: false,
      electronicDataTransfer: false,
      docPrinted: true,
      haveProcedure: false,
      haveMedicaments: false,
      haveProcedureGroups: false,
      language: 'de',
      countryId: countries ? countries[0].value : undefined
    };
  };

  // TODO KJ: this code stays commented in case we need guarantor info modal in future

  // onAdditionalInfoModalOpen = (form: FormApi) => {
  //
  //   const formValues = form.getState().values;
  //
  //   const guarantorAdditionalInfo: GuarantorAdditionalInfo = {
  //     collectiveBill: formValues.collectiveBill,
  //     billDiscount: formValues.billDiscount,
  //     billDetail: formValues.billDetail
  //   };
  //
  //   this.setState({
  //     guarantorAdditionalInfo: guarantorAdditionalInfo,
  //     infoModalOpen: true
  //   });
  //
  // };
  //
  // onAdditionalInfoModalClose = (form: FormApi, cancel: boolean) => {
  //
  //   const {guarantorAdditionalInfo} = this.state;
  //
  //   if (cancel) {
  //     const formValues = form.getState().values;
  //
  //     const guarantorFormValues: Partial<UpsertGuarantorDto> = {
  //       ...formValues,
  //       ...guarantorAdditionalInfo
  //     };
  //
  //     form.reset(guarantorFormValues);
  //   }
  //
  //   this.setState({
  //     infoModalOpen: false
  //   });
  //
  // };

  mapZipData = (field: string, zipData: IndexedZipData[], orderBy: ZipDataOrderBy): DropdownOption[] => {

    const sortedZipData = sortZipData(zipData, orderBy);

    return sortedZipData.map(data => ({
      key: data.id,
      text: data[field],
      value: `${data.zip}, ${data.city}, ${data.countryId}`,
      content: `${data.zip}, ${data.city}, ${data.canton}`,
      predefinedOption: true
    }))
  };

  searchZipData = (query: string, countryId: number) => {

    const {cities, zip: zips, cancelTokenSource} = this.state;

    searchZipDataForInput(query, countryId, cancelTokenSource, 75)
        .then(response => {

          const allInitialAndNewCityAdditions = cities.filter(city => city.value == city.key);
          const allInitialAndNewZipAdditions = zips.filter(zip => zip.value == zip.key);

          this.setState({
            cities: [...allInitialAndNewCityAdditions, ...this.mapZipData("city", response, ZipDataOrderBy.CITY)],
            zip: [...allInitialAndNewZipAdditions, ...this.mapZipData("zip", response, ZipDataOrderBy.ZIP)]
          })
        })
        .catch(() => {
          this.setState({
            cities: [],
            zip: []
          })
        })
  };

  onNewAddition = (options: "zip" | "cities") => (newAddition: DropdownOption) => {

    const {cities, zip: zips} = this.state;

    if (options == "cities") {

      const foundCity = cities.find(city => city.key == newAddition.key);

      if (!foundCity) {
        newAddition.predefinedOption = false;
        const newCities = cities.filter(city => city.predefinedOption === true);
        this.setState({
          cities: [newAddition, ...newCities]
        });
      }
    } else if (options == "zip") {

      const foundZip = zips.find(zip => zip.key == newAddition.key);

      if (!foundZip) {
        newAddition.predefinedOption = false;
        const newZips = zips.filter(city => city.predefinedOption === true);
        this.setState({
          zip: [newAddition, ...newZips]
        });
      }
    }
  };

  updateZipDataFieldDecorator = (field: string) =>
      (zipData?: string, allValues?: Partial<UpsertPatientDto & IndexSignature>) => {

        if (zipData) {
          const splitZipData = zipData.split(", ");

          if (splitZipData.length == 3 && allValues && allValues[field] != zipData) {
            return zipData;
          }
        }

        return allValues ? allValues[field] : undefined;
      };

  zipDataDecorator = createDecorator(
      {
        field: "city",
        updates: {
          zip: this.updateZipDataFieldDecorator("zip")
        }
      },
      {
        field: "zip",
        updates: {
          city: this.updateZipDataFieldDecorator("city")
        }
      }
  );

  handleSubmit = async (values: Partial<UpsertGuarantorDto>) => {
    const {guarantorId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    let zip = "";
    let city = "";

    if (values.zip) {
      if (values.zip.includes(', ')) {
        zip = values.zip.split(", ")[0];
      } else {
        zip = values.zip;
      }
    }

    if (values.city) {
      if (values.city.includes(', ')) {
        city = values.city.split(", ")[1];
      } else {
        city = values.city;
      }
    }

    const upsertGuarantorDto = {
      ...values,
      zip: zip,
      city: city
    };

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (guarantorId) {
      await editGuarantor(guarantorId, upsertGuarantorDto, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    } else {
      await saveGuarantor(upsertGuarantorDto, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: string[] = [
      errorUtils.invalidInput,
      errorUtils.invalidEmail
    ];

    const violations: any[] = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {guarantor = this.initialGuarantorValues()} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            decorators={[this.zipDataDecorator]}
            initialValues={guarantor}
            subscription={{pristine: true, submitting: true, values: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({form, handleSubmit, submitting, values}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                {this.renderBasicInformationSection(form)}
                {this.renderCollectiveBillSection(values)}
                {this.renderBillingOptionsSection()}
                {this.renderPrintAndCalendariumOptionsSegment()}
              </PraxFormGrid>
              {/*{this.renderAdditionalInfoModal(form)}*/}
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>
                {getMessage("button.save")}
              </Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}
              </Button>
              {/*<Button compact type="button" floated="right" primary*/}
              {/*        onClick={() => this.onAdditionalInfoModalOpen(form)}*/}
              {/*        disabled={submitting}>*/}
              {/*  {getMessage("info")}*/}
              {/*</Button>*/}
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderBasicInformationSection = (form: FormApi): React.ReactNode => {

    const {
      countries,
      billTexts,
      cities,
      zip,
      tariffTypes,
      alternativeTaxPointGroups
    } = this.state;

    const {getMessage} = this.props;

    const validator = (value: string) => required(value);

    const selectMap: IndexSignature = {
      countryId: countries,
      language: billTexts,
      city: cities,
      zip: zip,
      tariffType: tariffTypes,
      alternativeTaxPointGroup: alternativeTaxPointGroups
    };

    const countryId = form.getState().values.countryId;

    const basicLeftInfoFields = [
      textInput({
        name: "title"
      }),
      textInput({
        name: "description",
        validate: validator
      }),
      textInput({
        name: "position"
      }),
      textInput({
        name: "section"
      }),
      textInput({
        name: "street"
      }),
      selectInput({
            name: "zip",
            validate: validator,
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: ((searchQuery: string) => this.searchZipData(searchQuery, countryId)),
            onNewAddition: (this.onNewAddition("zip"))
          },
          selectMap
      ),
      selectInput({
            name: "city",
            validate: validator,
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: ((searchQuery: string) => this.searchZipData(searchQuery, countryId)),
            onNewAddition: (this.onNewAddition("cities"))
          },
          selectMap
      ),
      selectInput({
            name: "countryId",
            validate: validator
          },
          selectMap
      )
    ];

    const basicRightInfoFields = [
      textInput({
        name: "tel"
      }),
      textInput({
        name: "fax"
      }),
      textInput({
        name: "email"
      }),
      textInput({
        name: "homepage"
      }),
      textInput({
        name: "eanNumber",
        validate: validator
      }),
      textInput({
        name: "recipientEanNumber",
        validate: validator
      }),
      textInput({
        name: "bagNumber"
      }),
      selectInput({
            name: "language",
            validate: validator
          },
          selectMap
      ),
      selectInput({
            name: "tariffType",
            clearable: true
          },
          selectMap
      ),
      selectInput({
            name: "alternativeTaxPointGroup",
            clearable: true
          },
          selectMap
      )
    ];

    const mappedLeftRow = basicLeftInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(fieldProps.name)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    const mappedRightRow = basicRightInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(fieldProps.name)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={10} verticalAlign="middle" textAlign="left">
              <Header as="h5">{getMessage("patient.basicInformation")}</Header>
            </Grid.Column>
            <Grid.Column width={6} textAlign="right">
              <Field
                  name="active"
                  component={CheckBox}
                  label={getMessage("guarantor.active")}
                  toggle
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedRightRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

  renderCollectiveBillSection = (values: any): React.ReactNode => {

    const {getMessage} = this.props;
    const {disableCollectiveBillGuarantor} = this.state;

    const disableCollectiveBill = values.tariffType || disableCollectiveBillGuarantor;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column style={{paddingTop: "0.5rem"}}>
              <Header as="h5">{getMessage("guarantor.collectiveBill.header")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={12}>
              <Field
                  name="collectiveBill"
                  component={CheckBox}
                  label={`${getMessage("guarantor.collectiveBill")}`}
                  disabled={disableCollectiveBill}
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };


  renderBillingOptionsSection = (): React.ReactNode => {

    const {diagnosis, billReceivers} = this.state;
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column style={{paddingTop: "0.5rem"}}>
              <Header as="h5">{getMessage("guarantor.billingOptions")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <StyledGridColumn>
              {`${getMessage("treatmentForm.billReceiver")}:`}
            </StyledGridColumn>
            <Grid.Column width={9}>
              <Field
                  name="bill"
                  component={RadioGroup}
                  radioDefinitions={billReceivers}
                  radiowidth="35%"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={2}>
              {`${getMessage("diagnosis")}:`}
            </Grid.Column>
            <Grid.Column width={10} style={{paddingRight: "27px", paddingLeft: "24px"}}>
              <Field
                  name="diagnosisCode"
                  component={RadioGroup}
                  radioDefinitions={diagnosis}
                  radiowidth="33%"
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

  renderPrintAndCalendariumOptionsSegment = (): React.ReactNode => {

    const {getMessage} = this.props;

    const rows: string[][] = [
      ["billText", "haveProcedureGroups"],
      ["electronicDataTransfer", "haveProcedure"],
      ["docPrinted", "haveMedicaments"]
    ];
    const mappedRows = rows.map((row, index) => {
      return (
          <Grid.Row key={index} style={{padding: "0.3rem"}}>
            {
              row.map((field, index1) => {
                return (
                    <React.Fragment key={index1}>
                      <Grid.Column width={8} verticalAlign="middle">
                        <Field
                            name={field}
                            component={CheckBox}
                            label={getMessage(`guarantor.${field}`)}
                        />
                      </Grid.Column>
                    </React.Fragment>
                );
              })
            }
          </Grid.Row>
      );
    });

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={8} style={{paddingTop: "0.5rem"}}>
              <Header as="h5">{getMessage("guarantor.print")}</Header>
            </Grid.Column>
            <Grid.Column width={8} style={{paddingTop: "0.5rem"}}>
              <Header as="h5">{getMessage("guarantor.calendarium")}</Header>
            </Grid.Column>
          </Grid.Row>
          {mappedRows}
        </React.Fragment>
    )
  };

  // TODO KJ: this code stays commented in case we need guarantor info modal in future

  // renderAdditionalInfoModal = (form: FormApi): React.ReactNode => {
  //
  //   const {infoModalOpen} = this.state;
  //   const {getMessage} = this.props;
  //
  //   const rows: string[][] = [
  //     ["billDiscount"]
  //   ];
  //
  //   const mappedRows = rows.map((row, index) => {
  //     return (
  //         <Grid.Row key={index}>
  //           {
  //             row.map((field, index1) => {
  //               let fieldProps: FieldProps = {
  //                 name: field,
  //                 component: Input
  //               };
  //
  //               return (
  //                   <React.Fragment key={index1}>
  //                     <Grid.Column width={4} verticalAlign="middle">
  //                       {`${getMessage(`guarantor.${field}`)}:`}
  //                     </Grid.Column>
  //                     <Grid.Column width={12} verticalAlign="middle">
  //                       <Field
  //                           fluid
  //                           {...fieldProps}
  //                       />
  //                     </Grid.Column>
  //                   </React.Fragment>
  //               );
  //             })
  //           }
  //         </Grid.Row>
  //     );
  //   });
  //
  //   return (
  //       <Modal
  //           open={infoModalOpen}
  //           onClose={() => this.onAdditionalInfoModalClose(form, true)}
  //           style={{width: "500px"}}
  //           closeOnDimmerClick={false}
  //       >
  //         <Modal.Header>{getMessage("guarantor.additionalInfo")}</Modal.Header>
  //         <FormModalContentContainer>
  //           <FormContainer>
  //             <FormContent>
  //               <PraxFormGrid>
  //                 <Grid.Row>
  //                   <Grid.Column width={4}>
  //                   </Grid.Column>
  //                   <Grid.Column width={12} verticalAlign="middle">
  //                     <Field
  //                         name="collectiveBill"
  //                         component={CheckBox}
  //                         label={getMessage("guarantor.collectiveBill")}
  //                     />
  //                   </Grid.Column>
  //                 </Grid.Row>
  //                 {mappedRows}
  //                 <Grid.Column width={4}>
  //                 </Grid.Column>
  //                 <Grid.Column width={12} verticalAlign="middle">
  //                   <Field
  //                       name="billDetail"
  //                       component={CheckBox}
  //                       label={getMessage("guarantor.billDetail")}
  //                   />
  //                 </Grid.Column>
  //               </PraxFormGrid>
  //             </FormContent>
  //
  //             <FormButtonsRow>
  //               <Button type="button" primary compact
  //                       onClick={() => this.onAdditionalInfoModalClose(form, false)}>
  //                 {getMessage("button.ok")}
  //               </Button>
  //               <Button type="button" compact
  //                       onClick={() => this.onAdditionalInfoModalClose(form, true)}>
  //                 {getMessage("button.cancel")}
  //               </Button>
  //             </FormButtonsRow>
  //
  //           </FormContainer>
  //         </FormModalContentContainer>
  //       </Modal>
  //   );
  // };
}

export default withMessages(UpsertGuarantorForm);
